import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import {
  Add,
  Delete,
  Visibility,
  Settings,
  Edit,
  AdminPanelSettings,
  Close
} from '@mui/icons-material';
import { API_CONFIG } from '../../config/api';
import Cookies from 'js-cookie';

const EnvironmentPermissionsDialog = ({ open, onClose, environment, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Add permission form
  const [addFormData, setAddFormData] = useState({
    user_id: '',
    permission_level: 'readonly'
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    if (open && environment) {
      loadPermissions();
      loadAvailableUsers();
    }
  }, [open, environment]);

  const loadPermissions = async () => {
    if (!environment) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = Cookies.get('auth_token');
      const response = await fetch(
        `${API_CONFIG.centralBackendURL}/api/v1/environments/${environment.id}/permissions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Je hebt geen rechten om permissions te bekijken');
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setPermissions(data.permissions || []);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setError(error.message || 'Fout bij laden van permissions');
    }
    setLoading(false);
  };

  const loadAvailableUsers = async () => {
    if (!environment) return;
    
    try {
      const token = Cookies.get('auth_token');
      const response = await fetch(
        `${API_CONFIG.centralBackendURL}/api/v1/environments/${environment.id}/available-users`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          console.warn('Geen rechten om beschikbare gebruikers te laden');
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setAvailableUsers(data.available_users || []);
    } catch (error) {
      console.error('Error loading available users:', error);
      // Don't set error state for this, as it's not critical
    }
  };

  const handleAddPermission = async () => {
    if (!addFormData.user_id) {
      setError('Selecteer een gebruiker');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      const response = await fetch(
        `${API_CONFIG.centralBackendURL}/api/v1/environments/${environment.id}/permissions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(addFormData)
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || `HTTP ${response.status}`);
      }

      setSuccess('Permission succesvol toegevoegd!');
      setAddFormData({ user_id: '', permission_level: 'readonly' });
      setShowAddForm(false);
      await loadPermissions();
      await loadAvailableUsers(); // Reload available users as one was just added
    } catch (error) {
      console.error('Error adding permission:', error);
      setError(error.message || 'Fout bij toevoegen van permission');
    }
    setLoading(false);
  };

  const handleRemovePermission = async (userId, userName) => {
    if (!window.confirm(`Weet je zeker dat je de toegang voor "${userName}" wilt intrekken?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      const response = await fetch(
        `${API_CONFIG.centralBackendURL}/api/v1/environments/${environment.id}/permissions/${userId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || `HTTP ${response.status}`);
      }

      setSuccess('Permission succesvol ingetrokken!');
      await loadPermissions();
    } catch (error) {
      console.error('Error removing permission:', error);
      setError(error.message || 'Fout bij intrekken van permission');
    }
    setLoading(false);
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'readonly': return <Visibility fontSize="small" />;
      case 'operator': return <Settings fontSize="small" />;
      case 'admin': return <Edit fontSize="small" />;
      case 'owner': return <AdminPanelSettings fontSize="small" />;
      default: return <Visibility fontSize="small" />;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'readonly': return 'default';
      case 'operator': return 'primary';
      case 'admin': return 'secondary';
      case 'owner': return 'success';
      default: return 'default';
    }
  };

  const getPermissionLabel = (permission) => {
    switch (permission) {
      case 'readonly': return 'Alleen lezen';
      case 'operator': return 'Operator';
      case 'admin': return 'Beheerder';
      case 'owner': return 'Eigenaar';
      default: return permission;
    }
  };

  const canManagePermissions = () => {
    return environment && ['owner', 'admin'].includes(environment.user_permission);
  };

  const canRemoveUser = (permission) => {
    // Kan owner niet verwijderen, en normale gebruiker kan niet admin/owner verwijderen
    if (permission.permission_level === 'owner') return false;
    if (environment.user_permission === 'admin' && permission.permission_level === 'admin') return false;
    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Toegangsrechten - {environment?.name}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Add Permission Form */}
            {canManagePermissions() && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Nieuwe gebruiker toevoegen
                </Typography>
                
                {!showAddForm ? (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setShowAddForm(true)}
                  >
                    Gebruiker Toevoegen
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel>Selecteer gebruiker</InputLabel>
                      <Select
                        value={addFormData.user_id}
                        label="Selecteer gebruiker"
                        onChange={(e) => setAddFormData(prev => ({ ...prev, user_id: e.target.value }))}
                      >
                        {availableUsers.length === 0 ? (
                          <MenuItem disabled>
                            <em>Geen beschikbare gebruikers</em>
                          </MenuItem>
                        ) : (
                          availableUsers.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {user.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.email}
                                  {user.company_roles.length > 0 && (
                                    <span> • {user.company_roles.map(r => r.company_name).join(', ')}</span>
                                  )}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Rechten</InputLabel>
                      <Select
                        value={addFormData.permission_level}
                        label="Rechten"
                        onChange={(e) => setAddFormData(prev => ({ ...prev, permission_level: e.target.value }))}
                      >
                        <MenuItem value="readonly">Alleen lezen</MenuItem>
                        <MenuItem value="operator">Operator</MenuItem>
                        <MenuItem value="admin">Beheerder</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Button
                      variant="contained"
                      onClick={handleAddPermission}
                      disabled={loading || !addFormData.user_id}
                    >
                      Toevoegen
                    </Button>
                    
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowAddForm(false);
                        setAddFormData({ user_id: '', permission_level: 'readonly' });
                      }}
                    >
                      Annuleren
                    </Button>
                  </Box>
                )}
              </Paper>
            )}

            {/* Permissions Table */}
            <Typography variant="h6" gutterBottom>
              Huidige gebruikers ({permissions.length})
            </Typography>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Gebruiker</TableCell>
                    <TableCell>Rechten</TableCell>
                    <TableCell>Toegekend op</TableCell>
                    <TableCell>Toegekend door</TableCell>
                    {canManagePermissions() && <TableCell align="right">Acties</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManagePermissions() ? 5 : 4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Geen gebruikers gevonden
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    permissions.map((perm) => (
                      <TableRow key={perm.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {perm.user.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {perm.user.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getPermissionIcon(perm.permission_level)}
                            label={getPermissionLabel(perm.permission_level)}
                            color={getPermissionColor(perm.permission_level)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(perm.granted_at).toLocaleDateString('nl-NL', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {perm.granted_by?.name || 'Systeem'}
                          </Typography>
                        </TableCell>
                        {canManagePermissions() && (
                          <TableCell align="right">
                            {canRemoveUser(perm) && (
                              <IconButton
                                color="error"
                                onClick={() => handleRemovePermission(perm.user.id, perm.user.name)}
                                disabled={loading}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnvironmentPermissionsDialog; 